import { Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

/**
 * Service to listen to localStorage changes across tabs and provides a way to get the changes.
 */

@Injectable({
    providedIn: 'root',
})
export class LocalStorageService implements OnDestroy {
    private readonly _changes$ = new Subject<LocalStorageChange>();
    private readonly _destroy$ = new Subject<void>();

    /** Emits whenever localStorage is modified (same tab or another tab/window). */
    public readonly changes$: Observable<LocalStorageChange> = this._changes$.asObservable();

    constructor() {
        this._listenToLocalStorageChanges();
    }

    /** Emits localStorage changes, optionally filtered to a single key. */
    public getChanges(key?: string): Observable<LocalStorageChange> {
        if (!key) {
            return this.changes$;
        }

        return this.changes$.pipe(filter((change: LocalStorageChange) => change.key === key));
    }

    private _listenToLocalStorageChanges(): void {
        fromEvent<StorageEvent>(window, 'storage')
            .pipe(
                takeUntil(this._destroy$),
                filter((event: StorageEvent) => event.storageArea === localStorage),
                map((event: StorageEvent) => ({
                    key: event.key,
                    previousValue: event.oldValue,
                    currentValue: event.newValue,
                }))
            )
            .subscribe((change: LocalStorageChange) => this._changes$.next(change));
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._changes$.complete();
    }
}

export interface LocalStorageChange {
    key: string | null;
    previousValue: string | null;
    currentValue: string | null;
}
